import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AnalyticEvents } from '@app/enums';
import { TimeOffPolicy, TimeOffSeniorityOverridePayload } from '@app/models/time-off-v3/time-off-policy.model';
import { TimeOffType } from '@app/models/time-off-v3/time-off-type.model';
import { TimeOffCreateFlowService } from '@app/modules/time-off-v3/services';
import { AnalyticService, NotifyService } from '@app/services';
import { TimeOffRequestRules } from '@models/time-off-v3/time-off-request-rules.model';
import { Subscription } from 'rxjs';

@Component({
    templateUrl: './policy-create.template.html',
    styleUrls: ['./policy-create.style.scss'],
})
export class PolicyCreateView implements OnInit {
    timeOffPolicy = new TimeOffPolicy();
    timeOffType = new TimeOffType();
    timeOffPolicySubscription!: Subscription;

    isLoading = false;

    constructor(
        private analyticService: AnalyticService,
        private timeOffCreateFlowService: TimeOffCreateFlowService,
        private notify: NotifyService,
        private route: ActivatedRoute,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.timeOffCreateFlowService.loadAllTimeOffApprovals();
        this.timeOffCreateFlowService.loadAllTimeOffTypes();
        this.timeOffCreateFlowService.updateTimeOffType(this.timeOffType);
        this.timeOffCreateFlowService.updateTimeOffPolicy(
            new TimeOffPolicy({
                ...this.timeOffCreateFlowService.timeOffPolicyDefaultValues,
                timeOffType: this.timeOffType,
            })
        );
        this.timeOffCreateFlowService.updateTimeOffRequestRules(
            new TimeOffRequestRules({
                minDayAmount: 0,
                maxLength: null,
                timeOffType: this.timeOffType,
            })
        );

        this.timeOffPolicySubscription = this.timeOffCreateFlowService.timeOffPolicy$.subscribe((policy) => {
            this.timeOffPolicy = policy;
        });
    }

    ngOnDestroy(): void {
        this.timeOffPolicySubscription.unsubscribe();
    }

    async onSave(): Promise<void> {
        this.isLoading = true;
        // Remove id so that we always store the data
        this.timeOffPolicy.id = undefined;

        // Clear time off type id if it's -1 since this is for matching on the mat-select
        try {
            if (this.timeOffPolicy.timeOffTypeId === -1) {
                const createdTimeOffType = await this.timeOffPolicy.timeOffType.save();
                this.timeOffPolicy.timeOffType = createdTimeOffType;
                this.timeOffPolicy.timeOffTypeId = createdTimeOffType.id;
            }

            if (!this.timeOffPolicy.isUnlimited) {
                this.timeOffPolicy.timeOffSeniorityOverridesPayload = this.getUpdatedSeniorityOverridesPayload();
            }
            const timeOffRequestRules = this.timeOffCreateFlowService.timeOffRequestRules;

            const createdTimeOffPolicy = await this.timeOffPolicy.save();

            if (timeOffRequestRules.isDirty()) {
                timeOffRequestRules.id = createdTimeOffPolicy.timeOffRequestRule.id;
                await timeOffRequestRules.save();
            }

            this.analyticService.trackEvent(AnalyticEvents.TimeOffTimeOffPolicyCreateTimeOffPolicy);
            this.notify.success('time-off-v3.timeOffPolicyHasBeenCreated');

            this.router.navigate(['/', 'time-off', 'v3', 'types-and-policies'], { relativeTo: this.route });
        } catch (err) {
            this.notify.error(err);
        } finally {
            this.isLoading = false;
        }
    }

    private getUpdatedSeniorityOverridesPayload(): Array<TimeOffSeniorityOverridePayload> {
        return this.timeOffPolicy.timeOffSeniorityOverrides.map((seniorityOverride) => seniorityOverride.toPayload());
    }
}
